/**
 * Offline verification of GBS ticket QR tokens.
 *
 * A ticket QR is `<reference>.<HS256 JWT>` (see splitScannedCode). The spec is
 * qr-token-scanner-integration.md and QrSigningKeys.md at the repo root, with
 * the binding rule from .claude/plans/qr-signing-keys.plan.md (Task 3.1):
 *
 *   header  { "alg": "HS256", "typ": "JWT", "kid": "bk-9001-v1" }
 *   payload { "barCode": "NUCO-ABC123-030052", "iat": 1757000000, "exp": 1757000010 }
 *
 *   1. kid from the header resolves a key from the device cache
 *   2. reject when now > key.expiresAt
 *   3. the base64url key is decoded to raw bytes - the encoded string is NOT the secret
 *   4. HMAC-SHA256 over `header.payload`, compared in constant time (subtle.verify)
 *   5. accept window `iat - leeway <= now <= exp + leeway`, against this device's clock
 *   6. `barCode` must equal the scanned reference, and the key's bookingReference
 *      must own that barcode: its leading booking segment, or for a transferred
 *      barcode (`BRAND-OLD-NNNNNN-NEW`) the new owner's `BRAND-NEW`. Without this a
 *      guest holding their own booking's key could sign any barcode in the event.
 *
 * The result strings are a contract shared with the api and admin - do not rename.
 *
 * Nothing here logs. A key is a bearer secret, and the token is not much less.
 */

import { splitScannedCode } from './scanUtils.js'

export const QR_VERIFICATION = Object.freeze({
  VERIFIED: 'verified',
  UNSIGNED: 'unsigned',
  INVALID: 'invalid',
  EXPIRED: 'expired',
  UNKNOWN_KEY: 'unknown_key',
  NOT_CHECKED: 'not_checked',
})

/** `qr-signature-mode` flag values. */
export const QR_MODE = Object.freeze({ OFF: 0, WARN: 1, ENFORCE: 2 })

/** GBS `leeway_seconds`; used when the key sync does not serve its own. */
export const DEFAULT_LEEWAY_SECONDS = 60

const textEncoder = new TextEncoder()

/**
 * @param {unknown} value
 * @returns {0|1|2} anything unrecognised is off
 */
export function normaliseQrMode(value) {
  const n = Number(value)
  return n === 1 || n === 2 ? n : 0
}

/**
 * Unpadded or padded base64url to bytes. Throws on anything else.
 * @param {string} value
 * @returns {Uint8Array}
 */
export function base64UrlToBytes(value) {
  if (typeof value !== 'string' || value.length === 0 || !/^[A-Za-z0-9_-]+={0,2}$/.test(value)) {
    throw new Error('not base64url')
  }
  const unpadded = value.replace(/=+$/, '')
  const padded = unpadded.replace(/-/g, '+').replace(/_/g, '/') +
    '='.repeat((4 - (unpadded.length % 4)) % 4)
  const binary = atob(padded)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes
}

function decodeJsonSegment(segment) {
  const bytes = base64UrlToBytes(segment)
  const parsed = JSON.parse(new TextDecoder().decode(bytes))
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('not a JSON object')
  }
  return parsed
}

/**
 * The bookings that may legitimately sign this barcode.
 *   NUCO-GNAC8V-030052            -> NUCO-GNAC8V
 *   WHITEROAD-BWBMKG-046624-5JESN3 -> WHITEROAD-BWBMKG, WHITEROAD-5JESN3
 * @param {string} barcode
 * @returns {string[]} upper-cased
 */
export function owningBookingRefs(barcode) {
  const parts = String(barcode || '').toUpperCase().split('-')
  if (parts.length < 2) return []
  const refs = [`${parts[0]}-${parts[1]}`]
  if (parts.length >= 4) refs.push(`${parts[0]}-${parts[parts.length - 1]}`)
  return refs
}

function getSubtle() {
  return globalThis.crypto?.subtle || null
}

/**
 * Verify the token half of a scanned code.
 *
 * @param {object} args
 * @param {string} args.reference  the leading reference (before the first dot)
 * @param {string|null} args.token the JWT after the first dot, or null
 * @param {(kid: string) => (Promise<object|null>|object|null)} args.getKey
 *   resolves `{ key, expiresAt, bookingReference }` or null
 * @param {number} [args.mode]     qr-signature-mode; 0 returns not_checked
 * @param {number} [args.now]      unix seconds, this device's clock
 * @param {number} [args.leewaySeconds]
 * @param {string[]} [args.bookingRefs] extra bookings that own this barcode
 *   (e.g. bookingRef from a cached ticket row)
 * @returns {Promise<string>} one of QR_VERIFICATION
 */
export async function verifyQrToken({
  reference,
  token,
  getKey,
  mode = QR_MODE.WARN,
  now = Math.floor(Date.now() / 1000),
  leewaySeconds = DEFAULT_LEEWAY_SECONDS,
  bookingRefs = [],
}) {
  if (normaliseQrMode(mode) === QR_MODE.OFF) return QR_VERIFICATION.NOT_CHECKED
  if (!token) return QR_VERIFICATION.UNSIGNED

  const parts = String(token).split('.')
  if (parts.length !== 3 || parts.some(p => p.length === 0)) return QR_VERIFICATION.INVALID
  const [headerB64, payloadB64, signatureB64] = parts

  let header, payload, signature
  try {
    header = decodeJsonSegment(headerB64)
    payload = decodeJsonSegment(payloadB64)
    signature = base64UrlToBytes(signatureB64)
  } catch {
    return QR_VERIFICATION.INVALID
  }

  // Pinned, never taken from the token: an attacker-chosen alg is the classic
  // JWT downgrade ("none", or HS256 against a public key).
  if (header.alg !== 'HS256') return QR_VERIFICATION.INVALID
  if (typeof header.kid !== 'string' || header.kid.length === 0) return QR_VERIFICATION.INVALID

  let keyRecord
  try {
    keyRecord = await getKey(header.kid)
  } catch {
    keyRecord = null
  }
  if (!keyRecord) return QR_VERIFICATION.UNKNOWN_KEY

  // A stale cache entry the purge has not reached yet.
  const keyExpiresAt = Number(keyRecord.expiresAt)
  if (Number.isFinite(keyExpiresAt) && now > keyExpiresAt) return QR_VERIFICATION.EXPIRED

  const subtle = getSubtle()
  let keyBytes
  try {
    keyBytes = base64UrlToBytes(keyRecord.key)
  } catch {
    // A corrupt cache entry says nothing about the ticket; let the server decide.
    return QR_VERIFICATION.UNKNOWN_KEY
  }
  if (!subtle) return QR_VERIFICATION.UNKNOWN_KEY

  let valid
  try {
    const cryptoKey = await subtle.importKey('raw', keyBytes, { name: 'HMAC', hash: 'SHA-256' }, false, ['verify'])
    valid = await subtle.verify('HMAC', cryptoKey, signature, textEncoder.encode(`${headerB64}.${payloadB64}`))
  } catch {
    return QR_VERIFICATION.UNKNOWN_KEY
  }
  if (!valid) return QR_VERIFICATION.INVALID

  // Binding. The claim is authoritative; the leading reference is the
  // backwards-compatible copy, so the two must agree.
  const barCode = typeof payload.barCode === 'string' ? payload.barCode : null
  if (!barCode || barCode.toUpperCase() !== String(reference || '').toUpperCase()) {
    return QR_VERIFICATION.INVALID
  }
  const keyBooking = String(keyRecord.bookingReference || '').toUpperCase()
  const owners = [...owningBookingRefs(barCode), ...bookingRefs.map(r => String(r || '').toUpperCase())]
  if (!keyBooking || !owners.includes(keyBooking)) return QR_VERIFICATION.INVALID

  // Acceptance window, either side widened by the leeway.
  const leeway = Number.isFinite(Number(leewaySeconds)) ? Number(leewaySeconds) : DEFAULT_LEEWAY_SECONDS
  const exp = Number(payload.exp)
  if (!Number.isFinite(exp)) return QR_VERIFICATION.INVALID
  if (now > exp + leeway) return QR_VERIFICATION.EXPIRED
  if (payload.iat !== undefined) {
    const iat = Number(payload.iat)
    if (!Number.isFinite(iat)) return QR_VERIFICATION.INVALID
    // Signed in the future as far as this device knows: a clock disagreement,
    // reported with the other time-window failure.
    if (now < iat - leeway) return QR_VERIFICATION.EXPIRED
  }

  return QR_VERIFICATION.VERIFIED
}

/**
 * Split a raw scan and verify it.
 * @param {string} raw the full scanned string
 * @param {object} options as verifyQrToken, minus reference/token
 * @returns {Promise<{raw: string, reference: string, token: string|null, verification: string}>}
 */
export async function verifyScannedCode(raw, options) {
  const { reference, token } = splitScannedCode(raw)
  const verification = await verifyQrToken({ ...options, reference, token })
  return { raw, reference, token, verification }
}

/**
 * What the scanner does with a result.
 *   warn (1):    tick on verified; non-blocking warning on invalid/expired/unknown_key
 *   enforce (2): as warn, but invalid/expired block locally. unknown_key never
 *                blocks (the cache may be stale - the server decides), nor does
 *                unsigned (the guest app falls back to a bare reference by design).
 * @returns {{block: boolean, warn: boolean, verified: boolean}}
 */
export function qrScanDecision(verification, mode) {
  const m = normaliseQrMode(mode)
  const bad = verification === QR_VERIFICATION.INVALID || verification === QR_VERIFICATION.EXPIRED
  const block = m === QR_MODE.ENFORCE && bad
  return {
    block,
    warn: m !== QR_MODE.OFF && !block && (bad || verification === QR_VERIFICATION.UNKNOWN_KEY),
    verified: m !== QR_MODE.OFF && verification === QR_VERIFICATION.VERIFIED,
  }
}

/**
 * The extra request fields for a scan. `scan` is the object the scanner
 * components emit as the second argument of `code-scanned`; manual entry has
 * none and adds nothing. When `code` is given it must be the scan's reference,
 * so a stale scan never rides along on a different request.
 * @returns {{qrToken?: string|null, clientQrVerification?: string}}
 */
export function qrRequestFields(scan, code) {
  if (!scan || typeof scan !== 'object') return {}
  if (code !== undefined && code !== null && scan.reference !== code) return {}
  return {
    qrToken: scan.token ?? null,
    clientQrVerification: scan.verification ?? QR_VERIFICATION.NOT_CHECKED,
  }
}

/**
 * The code to put in the request body: the full scanned string (the server
 * strips the token itself), or the given value when this was not a scan.
 */
export function qrRequestCode(scan, fallback) {
  if (!scan || typeof scan.raw !== 'string' || !scan.raw) return fallback
  // Only when the scan is the code being sent - never a stale one.
  if (fallback !== undefined && fallback !== null && scan.reference !== fallback) return fallback
  return scan.raw
}
